/**
 * Accent Color Utility
 * Applies the user's selected accent color to CSS variables across the UI.
 * Used on login, dashboard mount, and when the user changes their accent preference.
 */

const STORAGE_KEY = 'oryxos-accent-color'
const DEFAULT_ACCENT = '#0078D4'

/**
 * Converts a hex color to a darker variant for hover states.
 * Darkens by reducing each channel by ~20%.
 */
function darken(hex: string, amount = 0.2): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const dr = Math.round(r * (1 - amount))
  const dg = Math.round(g * (1 - amount))
  const db = Math.round(b * (1 - amount))
  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`
}

/**
 * Applies the accent color to the document's CSS custom properties.
 * Updates --oryxos-cta (buttons), --accent-color, and --oryxos-glow.
 */
export function applyAccentColor(color: string): void {
  if (!color || typeof document === 'undefined') return
  const root = document.documentElement
  root.style.setProperty('--oryxos-cta', color)
  root.style.setProperty('--oryxos-cta-hover', darken(color, 0.18))
  root.style.setProperty('--oryxos-cta-active', darken(color, 0.35))
  root.style.setProperty('--accent-color', color)
  root.style.setProperty('--accent-color-light', `${color}20`)
  root.style.setProperty('--oryxos-glow', `${color}26`)
  // Persist to localStorage for instant apply on next page load
  try { localStorage.setItem(STORAGE_KEY, color) } catch {}
}

/**
 * Loads accent color from localStorage and applies it immediately.
 * Call this early (e.g., in layout or before paint) to avoid flash.
 */
export function loadAccentFromStorage(): void {
  if (typeof document === 'undefined') return
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && stored !== DEFAULT_ACCENT) {
      applyAccentColor(stored)
    }
  } catch {}
}

/**
 * Gets the currently stored accent color.
 */
export function getStoredAccent(): string {
  if (typeof localStorage === 'undefined') return DEFAULT_ACCENT
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_ACCENT
  } catch {
    return DEFAULT_ACCENT
  }
}
